import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserPresence, UserStatus } from '@/types/presence';
import { useAuth } from '@/contexts/AuthContext';
import { globalCache } from '@/utils/cache';
import { PerformanceLogger } from '@/utils/performanceLogger';
import { activityTracker } from '@/utils/globalActivityTracker';

interface UseOptimizedPresenceReturn {
  getPresence: (userId: string) => UserPresence | null;
  myPresence: UserPresence | null;
  updateMyStatus: (status: UserStatus, message?: string) => Promise<void>;
  updateActivity: () => Promise<void>;
  isLoading: boolean;
  getVisiblePresences: (userIds: string[]) => Map<string, UserPresence>;
}

const ACTIVITY_TIMEOUT_MINUTES = 5;

export const useOptimizedPresence = (): UseOptimizedPresenceReturn => {
  const { user } = useAuth();
  const [presenceMap, setPresenceMap] = useState<Map<string, UserPresence>>(new Map());
  const [myPresence, setMyPresence] = useState<UserPresence | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [missingIds, setMissingIds] = useState<Set<string>>(new Set());
  const isMounted = useRef(true);

  // Optimized presence fetcher - memoized to prevent infinite loops
  const fetchPresencesForUsers = useMemo(() => async (userIds: string[]) => {
    if (!userIds.length) return;

    const timer = PerformanceLogger.startTimer('fetchPresencesForUsers');
    
    try {
      const cacheKey = `presence_batch_${userIds.sort().join(',')}`; 
      const cached = globalCache.get<UserPresence[]>(cacheKey);
      
      if (cached) {
        timer.end({ cached: true, userCount: userIds.length });
        setPresenceMap(prev => {
          const newMap = new Map(prev);
          cached.forEach(presence => newMap.set(presence.user_id, presence));
          return newMap;
        });
        return;
      }

      const { data, error } = await supabase
        .from('user_presence')
        .select('user_id, status, is_active, last_seen, activity_timeout, status_message, updated_at')
        .in('user_id', userIds);

      if (error) throw error;

      const presences = (data as UserPresence[]) || [];
      globalCache.set(cacheKey, presences, 300000); // ✅ 5 minutes cache - réduire pression DB

      setPresenceMap(prev => {
        const newMap = new Map(prev);
        presences.forEach(presence => {
          newMap.set(presence.user_id, presence);
          if (user && presence.user_id === user.id) {
            setMyPresence(presence);
          }
        });
        return newMap;
      });

      timer.end({ cached: false, userCount: userIds.length, fetchedCount: presences.length });
    } catch (error) {
      timer.end({ error: true });
      console.error('Error fetching user presences:', error);
    }
  }, [user?.id]);

  // Initialize only current user's presence - using useMemo for stability
  const initializeMyPresence = useMemo(() => async () => {
    if (!user) return;

    try {
      const { data: existingPresence } = await supabase
        .from('user_presence')
        .select('*')
        .eq('user_id', user.id)
        .single();

      // Use upsert to preserve existing status
      await supabase
        .from('user_presence')
        .upsert({
          user_id: user.id,
          is_active: true,
          last_seen: new Date().toISOString(),
          activity_timeout: new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString()
          // Status is NOT included = preserved if exists, DB default ('available') if new
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        });
    } catch (error) {
      console.error('Error initializing presence:', error);
    }
  }, [user]);

  // Optimized activity update - DO NOT modify status
  const updateActivity = useMemo(() => async () => {
    if (!user) return;

    try {
      // Only update activity fields, never the status
      await supabase
        .from('user_presence')
        .update({
          last_seen: new Date().toISOString(),
          activity_timeout: new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString(),
          is_active: true
        })
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  }, [user]);

  // Update status - using useMemo for stability
  const updateMyStatus = useMemo(() => async (status: UserStatus, message?: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_presence')
        .upsert({
          user_id: user.id,
          status,
          status_message: message || null,
          is_active: status !== 'offline',
          last_seen: new Date().toISOString(),
          activity_timeout: status !== 'offline' 
            ? new Date(Date.now() + ACTIVITY_TIMEOUT_MINUTES * 60 * 1000).toISOString()
            : null
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  }, [user]);

  // Optimized getter functions
  const getPresence = useCallback((userId: string): UserPresence | null => {
    return presenceMap.get(userId) || null;
  }, [presenceMap]);

  // Pure getter function - no side effects to prevent infinite loops
  const getVisiblePresences = useCallback((userIds: string[]): Map<string, UserPresence> => {
    const visiblePresences = new Map<string, UserPresence>();
    const missingUserIds: string[] = [];

    userIds.forEach(userId => {
      const presence = presenceMap.get(userId);
      if (presence) {
        visiblePresences.set(userId, presence);
      } else {
        missingUserIds.push(userId);
      }
    });

    // Track missing IDs for background fetch (no immediate side effects)
    if (missingUserIds.length > 0) {
      setMissingIds(prev => {
        const newSet = new Set(prev);
        missingUserIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }

    return visiblePresences;
  }, [presenceMap]); // Only depend on presenceMap

  // 🔥 PHASE 1: Polling 2 min au lieu de Realtime + presenceOptimizer
  useEffect(() => {
    if (!user) return;

    // Utiliser polling au lieu de Realtime
    import('@/utils/realtimeOptimizer').then(({ createPollingInterval }) => {
      createPollingInterval(
        'user_presence',
        async () => {
          console.log('📊 Polling user presence...');
          // Recharger toutes les presences
          try {
            const { data, error } = await supabase
              .from('user_presence')
              .select('user_id, status, is_active, last_seen, activity_timeout, status_message, updated_at')
              .limit(100);

            if (!error && data) {
              const presences = data as UserPresence[];
              
              // ✅ Filtrer les présences avec timeout valide
              const validPresences = presences.filter(p => {
                if (!p.activity_timeout) return false;
                return new Date(p.activity_timeout) > new Date();
              });
              
              setPresenceMap(prev => {
                const newMap = new Map(prev);
                validPresences.forEach(presence => {
                  newMap.set(presence.user_id, presence);
                  if (user && presence.user_id === user.id) {
                    setMyPresence(presence);
                  }
                });
                return newMap;
              });
              
              console.log(`✅ Loaded ${validPresences.length}/${presences.length} valid presences`);
            }
          } catch (error) {
            console.error('Error polling presences:', error);
          }
        },
        true
      );
    });

  }, [user?.id]);

  // Enhanced background fetch with smarter batching (300ms debounce)
  useEffect(() => {
    if (missingIds.size === 0) return;

    const timer = setTimeout(() => {
      const idsToFetch = Array.from(missingIds);
      setMissingIds(new Set()); // Clear the queue
      
      if (idsToFetch.length > 0) {
        // Cache presences with 2-minute TTL
        const cacheKey = `presences_batch_${idsToFetch.sort().join(',')}`;
        if (!globalCache.has(cacheKey)) {
          fetchPresencesForUsers(idsToFetch);
          globalCache.set(cacheKey, true, 120000); // 2 minutes
        }
      }
    }, 300); // Increased to 300ms for better batching

    return () => clearTimeout(timer);
  }, [missingIds, fetchPresencesForUsers]);

  // Initialize on mount - load all active presences
  useEffect(() => {
    if (user) {
      const initialize = async () => {
        setIsLoading(true);
        await initializeMyPresence();
        
        // ✅ Load all active presences at startup
        try {
          const { data: allPresences } = await supabase
            .from('user_presence')
            .select('user_id, status, is_active, last_seen, activity_timeout, status_message, updated_at')
            .eq('is_active', true)
            .gt('activity_timeout', new Date().toISOString());
          
          if (allPresences) {
            setPresenceMap(prev => {
              const newMap = new Map(prev);
              (allPresences as UserPresence[]).forEach(p => newMap.set(p.user_id, p));
              return newMap;
            });
            console.log('✅ Loaded initial presences:', allPresences.length);
          }
        } catch (error) {
          console.error('Error loading initial presences:', error);
        }
        
        setIsLoading(false);
      };
      initialize();
    }
  }, [user?.id, initializeMyPresence]);

  // 🎯 PHASE 1: ARRÊT D'URGENCE - Event listeners désactivés
  // Activity tracking with global singleton (15min throttle)
  useEffect(() => {
    if (!user) return;
    
    // Utiliser le singleton global au lieu des event listeners individuels
    activityTracker.setUpdateCallback(updateActivity);
    
    console.log('✅ Using global activity tracker (15min throttle, visibilitychange only)');
    
    // Pas de cleanup ici - géré par le singleton
    return () => {
      // Le singleton persiste entre les composants
    };
  }, [user?.id, updateActivity]);

  // Page visibility optimization
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Reduce activity when page is not visible
        return;
      } else {
        // Update activity when page becomes visible
        updateActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id, updateActivity]);

  // Cleanup on unmount with isMounted guard
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      
      // Only set offline on true unmount, not on user object changes
      setTimeout(() => {
        if (!isMounted.current && user) {
          supabase
            .from('user_presence')
            .update({
              status: 'offline',
              is_active: false
            })
            .eq('user_id', user.id);
        }
      }, 100);
    };
  }, []);

  return {
    getPresence,
    myPresence,
    updateMyStatus,
    updateActivity,
    isLoading,
    getVisiblePresences
  };
};